import { inject, Injectable, Renderer2, signal } from '@angular/core'
import { Router } from '@angular/router'
import {
    AuthStateService,
    UserOrganizationStateService,
} from '@baboor/baboor-auth'
import { Organization } from '@baboor/common-auth'
import { AlertService } from '@baboor/common-services'
import { TranslocoService } from '@jsverse/transloco'
import { WA_LOCATION } from '@ng-web-apis/common'

@Injectable()
export class HeaderOrganizationService {
    private authStateService = inject(AuthStateService)
    private userGroupStateService = inject(UserOrganizationStateService)
    private router = inject(Router)
    private alertService = inject(AlertService)
    private translocoService = inject(TranslocoService)
    private readonly location = inject(WA_LOCATION)

    isOrgDropdownVisible = signal(false)
    isLoading = signal(false)
    isError = signal(false)
    selectedGroupId = signal<string | null>(null)
    organization = signal<Organization | null>(null)

    constructor() {
        this.organization.set(
            this.authStateService.getOrganization() as Organization,
        )
    }

    toggleOrgDropdown(renderer: Renderer2, orgChevron?: any) {
        this.isOrgDropdownVisible.set(!this.isOrgDropdownVisible())
        if (orgChevron) {
            const chevronEl = orgChevron.nativeElement
            if (chevronEl.classList.contains('rotate-90')) {
                renderer.removeClass(chevronEl, 'rotate-90')
            } else {
                renderer.addClass(chevronEl, 'rotate-90')
            }
        }
    }

    onSwitchGroup(id: string, orgChevron?: any) {
        this.isLoading.set(true)
        this.selectedGroupId.set(id)
        this.authStateService.switchOrg(id).subscribe({
            next: () => {
                this.isLoading.set(false)
                this.isOrgDropdownVisible.set(false)
                if (orgChevron) {
                    const chevronEl = orgChevron.nativeElement
                    chevronEl.classList.remove('rotate-90')
                }
                const groupType = this.authStateService.getGroupType()
                this.alertService.success(
                    this.translocoService.translate(
                        'vendorDashboard.header.organizationSwitchSuccess',
                    ),
                )
                if (groupType === 'client') {
                    this.router
                        .navigateByUrl('/dashboard/home/client')
                        .then(() => {
                            this.location.reload()
                        })
                    return
                }
                if (groupType === 'vendor') {
                    this.router.navigateByUrl('/dashboard/home').then(() => {
                        this.location.reload()
                    })
                    return
                }
                window.location.href = '/dashboard/home'
            },
            error: () => {
                this.isLoading.set(false)
                this.selectedGroupId.set(null)
                this.isError.set(true)
                this.alertService.error(
                    this.translocoService.translate(
                        'vendorDashboard.header.organizationSwitchFailed',
                    ),
                )
            },
        })
    }

    onLoadOwnedGroups(
        moreOptionWrapper: HTMLDivElement,
        chevron: HTMLSpanElement,
    ) {
        this.userGroupStateService.loadMyGroups().subscribe({
            next: ({ data }) => {
                const groupCount = data.groups?.length || 0
                const createButtonCount = [
                    data.hasVendorGroup,
                    data.hasClientGroup,
                ].filter((has) => !has).length
                const itemHeight = 40
                const baseHeight =
                    groupCount * itemHeight + createButtonCount * itemHeight
                const calculatedHeight = Math.max(baseHeight, 120)
                moreOptionWrapper.classList.toggle('h-0')
                chevron.classList.toggle('rotate-90')
                if (moreOptionWrapper.classList.contains('h-0')) {
                    moreOptionWrapper.style.height = '0px'
                } else {
                    moreOptionWrapper.style.height = `${calculatedHeight}px`
                }
            },
            error: (error) => {
                console.error('Failed to load groups:', error)
            },
        })
    }
}
